/**
 * AirlineContext - Global state management for airline selection
 *
 * This context provides:
 * - Current selected airline
 * - User's available airlines (with roles)
 * - Functions to switch airlines
 * - Session persistence
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { GLOBAL_AIRLINE_ID } from '../lib/constants';

// =====================================================================
// Types
// =====================================================================

export interface UserAllowedAirline {
  user_id: string;
  airline_id: string;
  code: string;
  name: string;
  role?: 'admin' | 'editor' | 'viewer';
  is_default?: boolean;
}

interface AirlineContextValue {
  selectedAirline: UserAllowedAirline | null;
  setSelectedAirline: (airline: UserAllowedAirline) => void;
  userAirlines: UserAllowedAirline[];
  visibleAirlines: UserAllowedAirline[];
  /** Airlines scoped to the current login session (set by Login). Falls back to visibleAirlines if not yet set. */
  sessionAirlines: UserAllowedAirline[];
  setSessionAirlines: (airlines: UserAllowedAirline[]) => void;
  isLoading: boolean;
  error: Error | null;
  isSuperAdmin: boolean;
  refreshAirlines: () => Promise<UserAllowedAirline[]>;
}

// =====================================================================
// Context
// =====================================================================

const AirlineContext = createContext<AirlineContextValue | undefined>(undefined);

// =====================================================================
// Provider Component
// =====================================================================

export const AirlineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [selectedAirline, setSelectedAirlineState] = useState<UserAllowedAirline | null>(null);
  const [sessionAirlinesState, setSessionAirlinesState] = useState<UserAllowedAirline[]>([]);
  const previousDefaultAirlineIdRef = useRef<string | null>(null);

  // ===================================================================
  // Fetch User Airlines
  // ===================================================================

  const {
    data: userAirlines = [],
    isLoading,
    error,
    refetch,
  } = useQuery<UserAllowedAirline[]>({
    queryKey: ['user-airlines', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch from v_user_allowed_airlines view
      const { data, error } = await supabase
        .from('v_user_allowed_airlines')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Enhance with role and is_default from memberships and profiles
      const { data: memberships } = await supabase
        .from('user_airline_memberships')
        .select('airline_id, role')
        .eq('user_id', user.id);

      const { data: profile } = await supabase
        .from('profiles')
        .select('default_airline_id')
        .eq('user_id', user.id)
        .single();

      return (data || []).map((airline) => {
        const membership = memberships?.find((m) => m.airline_id === airline.airline_id);
        return {
          ...airline,
          role: membership?.role as 'admin' | 'editor' | 'viewer',
          is_default: airline.airline_id === profile?.default_airline_id,
        };
      });
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    // Fallback sync for cross-client updates when realtime events are unavailable.
    refetchInterval: user?.id ? 15000 : false,
  });

  // ===================================================================
  // Check if User is Super Admin
  // ===================================================================

  const visibleAirlines = React.useMemo(
    () => userAirlines.filter((a) => a.airline_id !== GLOBAL_AIRLINE_ID),
    [userAirlines]
  );

  const isSuperAdmin = React.useMemo(() => {
    const globalMembership = userAirlines.find(
      (a: UserAllowedAirline) => a.airline_id === GLOBAL_AIRLINE_ID && a.role === 'admin'
    );
    return !!globalMembership;
  }, [userAirlines]);

  // ===================================================================
  // Load Selected Airline from Session Storage
  // ===================================================================

  useEffect(() => {
    if (!user?.id || userAirlines.length === 0) return;

    const selectedStillValid = selectedAirline
      ? userAirlines.some((a: UserAllowedAirline) => a.airline_id === selectedAirline.airline_id)
      : false;

    // 1. Prefer the user's configured default airline when it changes
    const defaultAirline = userAirlines.find((a: UserAllowedAirline) => a.is_default);
    const currentDefaultAirlineId = defaultAirline?.airline_id ?? null;
    const defaultChanged = previousDefaultAirlineIdRef.current !== currentDefaultAirlineId;
    previousDefaultAirlineIdRef.current = currentDefaultAirlineId;

    if (defaultAirline && (!selectedStillValid || !selectedAirline || defaultChanged)) {
      setSelectedAirlineState(defaultAirline);
      return;
    }

    // 2. If current selection is missing/invalid, fall back to session storage
    if (!selectedStillValid || !selectedAirline) {
      const stored = sessionStorage.getItem(`selected_airline_${user.id}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as { airline_id?: string };
          const storedAirline = userAirlines.find(
            (a: UserAllowedAirline) => a.airline_id === parsed.airline_id
          );
          if (storedAirline) {
            setSelectedAirlineState(storedAirline);
            return;
          }
        } catch (e) {
          console.error('Failed to parse stored airline:', e);
        }
      }

      // 3. No default and no valid stored selection — pick first real (non-global) airline
      const firstReal = visibleAirlines[0] ?? userAirlines[0];
      if (firstReal) setSelectedAirlineState(firstReal);
    }
  }, [user?.id, userAirlines, visibleAirlines, selectedAirline?.airline_id]);

  // ===================================================================
  // Save Selected Airline to Session Storage
  // ===================================================================

  useEffect(() => {
    if (selectedAirline && user?.id) {
      sessionStorage.setItem(
        `selected_airline_${user.id}`,
        JSON.stringify(selectedAirline)
      );
      console.log('[AirlineContext] Selected airline:', selectedAirline.name);
    }
  }, [selectedAirline, user?.id]);

  // Keep airline context in sync when default airline changes in another client (web/mobile).
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`profile-default-airline-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const previousDefaultAirlineId =
            (payload.old as { default_airline_id?: string | null } | null)?.default_airline_id ??
            null;
          const nextDefaultAirlineId =
            (payload.new as { default_airline_id?: string | null } | null)?.default_airline_id ??
            null;

          if (previousDefaultAirlineId !== nextDefaultAirlineId) {
            void refetch();
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, refetch]);

  // ===================================================================
  // Set Selected Airline (with validation)
  // ===================================================================

  const setSelectedAirline = (airline: UserAllowedAirline) => {
    // Verify user has access to this airline
    const hasAccess = userAirlines.some((a: UserAllowedAirline) => a.airline_id === airline.airline_id);
    if (!hasAccess) {
      console.error('[AirlineContext] User does not have access to airline:', airline);
      return;
    }

    setSelectedAirlineState(airline);
  };

  const setSessionAirlines = (airlines: UserAllowedAirline[]) => {
    setSessionAirlinesState(airlines.filter((a) => a.airline_id !== GLOBAL_AIRLINE_ID));
  };

  // Reset session scope on sign-out
  React.useEffect(() => {
    if (!user) {
      setSessionAirlinesState([]);
    }
  }, [user]);

  const refreshAirlines = async (): Promise<UserAllowedAirline[]> => {
    const { data } = await refetch();
    return data ?? [];
  };

  // ===================================================================
  // Context Value
  // ===================================================================

  // Falls back to all visible airlines when Login hasn't set a session scope yet
  const sessionAirlines = sessionAirlinesState.length > 0 ? sessionAirlinesState : visibleAirlines;

  const value: AirlineContextValue = {
    selectedAirline,
    setSelectedAirline,
    userAirlines,
    visibleAirlines,
    sessionAirlines,
    setSessionAirlines,
    isLoading,
    error: error as Error | null,
    isSuperAdmin,
    refreshAirlines,
  };

  return <AirlineContext.Provider value={value}>{children}</AirlineContext.Provider>;
};

// =====================================================================
// Hook
// =====================================================================

export const useAirline = (): AirlineContextValue => {
  const context = useContext(AirlineContext);
  if (!context) {
    throw new Error('useAirline must be used within an AirlineProvider');
  }
  return context;
};

// =====================================================================
// Utility Hooks
// =====================================================================

/**
 * Hook to check if current user has a specific role in selected airline
 */
export const useAirlineRole = (requiredRole: 'admin' | 'editor' | 'viewer'): boolean => {
  const { selectedAirline } = useAirline();

  const roleHierarchy = { admin: 3, editor: 2, viewer: 1 };

  if (!selectedAirline?.role) return false;

  return roleHierarchy[selectedAirline.role] >= roleHierarchy[requiredRole];
};

/**
 * Hook to check if current user can edit content in selected airline
 */
export const useCanEdit = (): boolean => {
  return useAirlineRole('editor');
};

/**
 * Hook to check if current user is admin of selected airline
 */
export const useIsAirlineAdmin = (): boolean => {
  return useAirlineRole('admin');
};
